// ============================================================================
// FRONT OFFICE WORKSPACE (E2 — FO-DASH-001 / FO-INHOUSE-001)
// ============================================================================
// Dedicated workspace for the front office team: today dashboard, arrivals /
// departures / in-house queues with inline lifecycle actions, walk-in capture
// (booking create chained into Quick Check-In), and the physical Room Board
// with housekeeping status updates and room moves.
//
// Check-in/check-out/no-show/extend-stay reuse the existing booking-module
// lifecycle modals — this screen fetches the full Booking row on demand and
// hands it over, so both entry points (Booking List and FO Workspace) share
// one implementation. Reference: crs-doc/CRS-Web-Enhancements-2026-07-FRD.md §3.

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ModuleHeader } from 'components/modals';
import type { ActionButton } from 'components/modals';
import { type ActionMenuItem, SyutGrid, type SyutGridColumn, SyutSelect, MinimizedModalStrip } from 'components/ui';
import type { SelectOption } from 'components/ui/SyutSelect';
import notificationService from 'services/notificationService';
import { useModalMinimize } from 'contexts/ModalMinimizeContext';
import { frontOfficeService } from 'api/services/frontOfficeService';
import { bookingService } from 'api/services/bookingService';
import { propertyService } from 'api/services/propertyService';
import type { Booking } from 'api/types/booking.types';
import type { DaySheetSummary, FrontOfficeDashboard, FrontOfficeQueueItem, RoomBoardRoom } from 'api/types/frontOffice.types';
import QuickCheckInModal from 'components/modals/modules/booking-module/QuickCheckInModal';
import WalkInModal from 'components/modals/modules/frontoffice-module/WalkInModal';
import CheckOutRoomPickerModal from 'components/modals/modules/frontoffice-module/CheckOutRoomPickerModal';
import MarkNoShowConfirmDialog from 'components/modals/modules/booking-module/MarkNoShowConfirmDialog';
import ExtendStayModal from 'components/modals/modules/booking-module/ExtendStayModal';
import BookingViewModal from 'components/modals/modules/booking-module/BookingViewModal';
import RoomStatusModal from 'components/modals/modules/frontoffice-module/RoomStatusModal';
import RoomMoveModal from 'components/modals/modules/frontoffice-module/RoomMoveModal';
import IssueInvoicesModal, { invoiceActions, openPdf } from 'components/modals/modules/pms-module/IssueInvoicesModal';
import { outputProfileService } from 'api/services/outputProfileService';
import RoomChargingModal, { canControlRoomCharging } from 'components/modals/modules/pms-module/RoomChargingModal';
import { folioService } from 'api/services/folioService';
import type { FolioDetail } from 'api/types/folio.types';
import { formCText } from 'api/types/checkin.types';
import AvailabilityCalendarPanel from 'components/modals/modules/booking-module/sections/AvailabilityCalendarPanel';

// ============================================================================
// TYPES & CONSTANTS
// ============================================================================

/*
 * Arrivals, In-House and Departures were three tabs. They are the same entity at
 * three points of one lifecycle, and splitting them forced the operator to know
 * which queue a guest was in before acting — but at a desk you have a person in
 * front of you and you want to find them. They are now one Day Sheet, grouped so
 * a queue can still be worked, with a search that flattens across all three.
 *
 * Dashboard and Room Board stay: one is a summary, the other a different
 * projection entirely (physical rooms, not guests).
 */
type FoTab = 'dashboard' | 'daysheet' | 'roomboard';

/** The three groups inside the Day Sheet. */
type SectionKey = 'arrivals' | 'inhouse' | 'dueout';
/**
 * 'yesterday' (Arrivals only) supports backdated processing: implementation /
 * data-migration entry of prior-day check-ins, and late-night guests who arrive
 * after midnight and would otherwise vanish from the current day's arrival list.
 */
type QueueDate = 'yesterday' | 'today' | 'tomorrow';

/** SyutGrid requires rows keyed by `id`. */
type QueueRow = FrontOfficeQueueItem & { id: string };

/** Per-section paging + collapse state inside the Day Sheet. */
interface SectionData {
  items: QueueRow[];
  total: number;
  page: number;
  loading: boolean;
  collapsed: boolean;
}

const EMPTY_SECTION: SectionData = {
  items: [], total: 0, page: 1, loading: false, collapsed: false,
};

const SECTION_META: Record<SectionKey, { title: string; hint: string; empty: string }> = {
  arrivals: {
    title: 'Expected arrivals',
    hint: 'Primary action · Check In',
    empty: 'No arrivals for the selected date.',
  },
  inhouse: {
    title: 'In-house',
    hint: 'Primary action · View',
    empty: 'No guests currently in-house.',
  },
  dueout: {
    title: 'Due out',
    hint: 'Primary action · Check Out',
    empty: 'No departures for the selected date.',
  },
};

interface FrontOfficeWorkspaceProps {
  moduleIcon?: string;
  tabId?: string;
  moduleName?: string;
  /** RBAC — defaults to [] (fail-closed). */
  allowedActions?: string[];
}

const STATUS_PILL_COLORS: Record<string, string> = {
  success: '#16a34a',
  danger: '#dc2626',
  warning: '#d97706',
  info: '#2563eb',
  secondary: '#64748b',
  dark: '#334155',
};

const BOARD_STATE_COLORS: Record<string, { bg: string; border: string; label: string }> = {
  AVAILABLE: { bg: 'rgba(22,163,74,0.10)', border: '#16a34a', label: 'Available' },
  OCCUPIED:  { bg: 'rgba(37,99,235,0.12)', border: '#2563eb', label: 'Occupied' },
  DIRTY:     { bg: 'rgba(217,119,6,0.12)', border: '#d97706', label: 'Dirty' },
  INSPECTED: { bg: 'rgba(13,148,136,0.12)', border: '#0d9488', label: 'Inspected' },
  BLOCKED:   { bg: 'rgba(100,116,139,0.12)', border: '#64748b', label: 'Blocked' },
  OOO:       { bg: 'rgba(220,38,38,0.10)', border: '#dc2626', label: 'Out of Order' },
};

const toDateInput = (d: Date): string => {
  const tz = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return tz.toISOString().slice(0, 10);
};

const queueDateValue = (which: QueueDate): string => {
  const d = new Date();
  if (which === 'tomorrow') d.setDate(d.getDate() + 1);
  else if (which === 'yesterday') d.setDate(d.getDate() - 1);
  return toDateInput(d);
};

const QUEUE_DATE_LABELS: Record<QueueDate, string> = {
  yesterday: 'Yesterday', today: 'Today', tomorrow: 'Tomorrow',
};

const fmtTime = (t?: string): string => (t ? t.slice(0, 5) : '—');
const fmtDate = (d?: string): string => (d ? d.slice(0, 10) : '—');

// ============================================================================
// COMPONENT
// ============================================================================

const FrontOfficeWorkspace: React.FC<FrontOfficeWorkspaceProps> = ({
  tabId, allowedActions = [],
}) => {
  const { isMinimized, dismiss: dismissMinimized, getMinimizedModal } = useModalMinimize();

  // --- Property context ---
  const [propertyOptions, setPropertyOptions] = useState<SelectOption[]>([]);
  const [propertyId, setPropertyId] = useState('');

  // --- Tabs & date toggle ---
  const [activeTab, setActiveTab] = useState<FoTab>('dashboard');
  const [queueDate, setQueueDate] = useState<QueueDate>('today');

  // --- Data ---
  const [dashboard, setDashboard] = useState<FrontOfficeDashboard | null>(null);
  /*
   * Section totals, aggregated server-side for the SELECTED date. The tab badge
   * reads from here too: it used to sum the dashboard tiles, which are always
   * for today, so it reported 24 while the sections below it showed 14 on a
   * Yesterday view. One source means they cannot disagree again.
   */
  const [summary, setSummary] = useState<DaySheetSummary | null>(null);
  // Each section paginates independently over its own endpoint. A combined
  // endpoint would have to return a union row and re-page three different
  // orderings; three calls is both simpler and closer to how the desk reads it.
  const [sections, setSections] = useState<Record<SectionKey, SectionData>>({
    arrivals: { ...EMPTY_SECTION },
    inhouse:  { ...EMPTY_SECTION },
    dueout:   { ...EMPTY_SECTION },
  });
  const [pageSize, setPageSize] = useState(25);

  // Search is the other half of the job: the guest at the counter, whose queue
  // the operator does not know. When it is active the groups give way to one
  // flat result list spanning all three states.
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<QueueRow[] | null>(null);
  const [searching, setSearching] = useState(false);
  const [boardRooms, setBoardRooms] = useState<RoomBoardRoom[]>([]);
  const [loading, setLoading] = useState(false);

  // --- Lifecycle modals (booking fetched on demand) ---
  const [checkInBooking, setCheckInBooking] = useState<Booking | null>(null);
  /** Post-check-in amend session (QuickCheckInModal in amend mode). */
  const [amendBooking, setAmendBooking] = useState<Booking | null>(null);
  const [checkOutBooking, setCheckOutBooking] = useState<Booking | null>(null);
  const [noShowBooking, setNoShowBooking] = useState<Booking | null>(null);
  const [extendBooking, setExtendBooking] = useState<Booking | null>(null);
  const [viewBooking, setViewBooking] = useState<Booking | null>(null);

  // --- Room board modals ---
  const [statusRoom, setStatusRoom] = useState<RoomBoardRoom | null>(null);
  const [moveRoom, setMoveRoom] = useState<RoomBoardRoom | null>(null);

  // --- Walk-in ---
  // ONE path: the single-screen WalkInModal (Aug 2026 — package-first, with the
  // commercial/allocation split). The full-booking-form escape hatch was
  // removed with it: two entry points left the operator unsure which was
  // authoritative, and the package selector now covers what the hatch was for.
  const [quickWalkInOpen, setQuickWalkInOpen] = useState(false);
  /**
   * Parent booking when the walk-in is opened via "Add Rooms (Walk-In)" on an
   * in-house row — the created booking carries related_booking_id so the group
   * stays visible as one party (and the future combined invoice can group).
   * The toolbar Walk-In button clears it (standalone walk-in).
   */
  const [walkInLink, setWalkInLink] = useState<{ id: string; number: string } | null>(null);

  const can = useCallback((action: string) => allowedActions.includes(action), [allowedActions]);

  // --- Load properties once; default to the first accessible property ---
  useEffect(() => {
    let cancelled = false;
    // includeInactive: false — the switcher must only offer operable properties
    // (backend PropertyQueryParameters defaults IncludeInactive to true).
    propertyService.getAll({ page: 1, pageSize: 100, includeInactive: false })
      .then((res) => {
        if (cancelled) return;
        const options = (res.data || []).map((p: any) => ({
          value: p.id,
          label: `${p.propertyCode} - ${p.propertyName}`,
        }));
        setPropertyOptions(options);
        if (options.length > 0) setPropertyId((prev) => prev || String(options[0].value));
      })
      .catch(() => { if (!cancelled) notificationService.toast.error('Failed to load properties.'); });
    return () => { cancelled = true; };
  }, []);

  // --- Data loaders ---
  // Stale-response guards: the layout keeps this workspace mounted across
  // module-tab switches and the first request per session can be slow
  // (tenant-connection resolution). Without a sequence check, a slow earlier
  // response resolving AFTER a fast one silently overwrites fresh data —
  // rows visibly "disappear". Only the latest request may write state.
  const sectionReqSeq = useRef<Record<SectionKey, number>>({ arrivals: 0, inhouse: 0, dueout: 0 });
  const boardReqSeq = useRef(0);
  const dashReqSeq = useRef(0);

  const loadDashboard = useCallback(async () => {
    if (!propertyId) return;
    const seq = ++dashReqSeq.current;
    try {
      const dto = await frontOfficeService.getDashboard(propertyId);
      if (seq === dashReqSeq.current) setDashboard(dto);
    } catch { /* keep the last good counts; queues surface their own errors */ }
  }, [propertyId]);

  const patchSection = useCallback((key: SectionKey, patch: Partial<SectionData>) => {
    setSections((prev) => ({ ...prev, [key]: { ...prev[key], ...patch } }));
  }, []);

  const loadSection = useCallback(async (
    key: SectionKey, pg: number, ps: number, which: QueueDate,
  ) => {
    if (!propertyId) return;
    const seq = ++sectionReqSeq.current[key];
    patchSection(key, { loading: true });
    try {
      const params = { propertyId, page: pg, pageSize: ps };
      const result =
        key === 'arrivals' ? await frontOfficeService.getArrivals({ ...params, date: queueDateValue(which) })
        : key === 'dueout' ? await frontOfficeService.getDepartures({ ...params, date: queueDateValue(which) })
        : await frontOfficeService.getInHouse(params);
      if (seq !== sectionReqSeq.current[key]) return; // superseded
      if (!result.success) {
        // Failed-but-parsed envelope: keep the current rows instead of blanking.
        notificationService.toast.error(result.message || 'Failed to load the list. Use Refresh to retry.');
        return;
      }
      patchSection(key, {
        items: result.data.map((item) => ({ ...item, id: item.bookingId })),
        total: result.pagination.totalItems,
        page: pg,
      });
    } catch {
      if (seq !== sectionReqSeq.current[key]) return;
      notificationService.toast.error('Failed to load the list. Use Refresh to retry.');
    } finally {
      if (seq === sectionReqSeq.current[key]) patchSection(key, { loading: false });
    }
  }, [propertyId, patchSection]);

  const loadDaySheet = useCallback((ps: number, which: QueueDate) => {
    (['arrivals', 'inhouse', 'dueout'] as SectionKey[]).forEach((k) => loadSection(k, 1, ps, which));
    if (!propertyId) return;
    frontOfficeService.getDaySheetSummary(propertyId, queueDateValue(which))
      .then(setSummary)
      // A missing badge is a cosmetic loss; the rows are the operational data.
      // Failing quietly beats a second error toast over the same reload.
      .catch(() => setSummary(null));
  }, [loadSection, propertyId]);

  const loadBoard = useCallback(async () => {
    if (!propertyId) return;
    const seq = ++boardReqSeq.current;
    setLoading(true);
    try {
      const rooms = await frontOfficeService.getRoomBoard(propertyId);
      if (seq === boardReqSeq.current) setBoardRooms(rooms);
    } catch {
      if (seq === boardReqSeq.current) notificationService.toast.error('Failed to load the room board.');
    } finally {
      if (seq === boardReqSeq.current) setLoading(false);
    }
  }, [propertyId]);

  // Bumped by refreshAll so the availability calendar (which fetches its own
  // window) refetches alongside the KPI tiles instead of serving stale cells.
  const [availRefreshToken, setAvailRefreshToken] = useState(0);

  const refreshAll = useCallback(() => {
    loadDashboard();
    setAvailRefreshToken(t => t + 1);
    if (activeTab === 'roomboard') loadBoard();
    else if (activeTab === 'daysheet') loadDaySheet(pageSize, queueDate);
  }, [activeTab, loadBoard, loadDashboard, loadDaySheet, pageSize, queueDate]);

  // The dashboard counts refresh alongside every queue/board load (any internal
  // tab switch, date toggle, page change or property change) — the workspace
  // stays mounted across module-tab switches, so a mount-time-only fetch goes
  // stale as soon as a booking is created elsewhere (bug: tabs showed "(0)"
  // while the arrivals list had rows).
  useEffect(() => {
    loadDashboard();
    if (activeTab === 'roomboard') loadBoard();
    else if (activeTab === 'daysheet') loadDaySheet(pageSize, queueDate);
  }, [activeTab, loadBoard, loadDashboard, loadDaySheet, pageSize, queueDate]);

  /*
   * Debounced desk search. Below two characters it clears rather than querying —
   * a one-character term matches most of the property and the operator would be
   * scrolling a result list instead of reading their queues.
   */
  useEffect(() => {
    const term = searchTerm.trim();
    if (term.length < 2) { setSearchResults(null); setSearching(false); return; }
    if (!propertyId) return;
    setSearching(true);
    const timer = setTimeout(() => {
      frontOfficeService.searchDaySheet(propertyId, term)
        .then((rows) => setSearchResults(rows.map((r) => ({ ...r, id: r.bookingId }))))
        .catch(() => notificationService.toast.error('Search failed. Try again.'))
        .finally(() => setSearching(false));
    }, 350);
    return () => clearTimeout(timer);
  }, [searchTerm, propertyId]);

  // --- Fetch full booking then open a lifecycle modal ---
  const withBooking = useCallback(async (bookingId: string, open: (b: Booking) => void) => {
    try {
      const res = await bookingService.getById(bookingId);
      if (res.data) open(res.data);
      else notificationService.toast.error('Booking could not be loaded.');
    } catch {
      notificationService.toast.error('Booking could not be loaded.');
    }
  }, []);

  const handleLifecycleDone = useCallback(() => { refreshAll(); }, [refreshAll]);

  // --- Walk-in chain: booking created → straight into Quick Check-In ---


  // --- Shared open handlers (used by the row-action menu AND the clickable cells) ---
  const openCheckIn = useCallback((row: QueueRow) => {
    // Restore a parked check-in session for this booking instead of opening
    // a fresh (empty) one.
    const modalId = tabId ? `${tabId}-checkin-modal-${row.bookingNumber}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    withBooking(row.bookingId, setCheckInBooking);
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized, withBooking]);

  const openAmend = useCallback((row: QueueRow) => {
    // Same restore-first contract as check-in, distinct modal id per mode.
    const modalId = tabId ? `${tabId}-amend-modal-${row.bookingNumber}` : null;
    if (modalId && isMinimized(modalId)) {
      const modal = getMinimizedModal(modalId);
      dismissMinimized(modalId);
      modal?.onRestore?.();
      return;
    }
    withBooking(row.bookingId, setAmendBooking);
  }, [tabId, isMinimized, getMinimizedModal, dismissMinimized, withBooking]);

  const [invoiceFor, setInvoiceFor] = useState<string | null>(null);
  /** Room charging off / credit limit of an in-house stay's folio (pms-inhouse posting-control). */
  const [chargingFolio, setChargingFolio] = useState<FolioDetail | null>(null);
  const openRoomCharging = useCallback(async (folioId: string) => {
    try {
      setChargingFolio(await folioService.get(folioId));
    } catch (err: any) {
      notificationService.toast.error(err?.response?.data?.message || err?.message || 'The folio did not load.');
    }
  }, []);
  const openCheckOut = useCallback((row: QueueRow) => {
    withBooking(row.bookingId, setCheckOutBooking);
  }, [withBooking]);

  const openView = useCallback((row: QueueRow) => {
    withBooking(row.bookingId, setViewBooking);
  }, [withBooking]);

  /**
   * The row's PRIMARY workflow — what clicking the Booking # / Status cell
   * triggers, so the desk doesn't need the action menu for the common case:
   * arrivals CONFIRMED → Check In; departures CHECKED_IN → Check Out;
   * everything else → View Details.
   */
  const primaryActionFor = useCallback((row: QueueRow, section: SectionKey): { label: string; run: () => void } | null => {
    if (row.bookingStatusCode === 'CONFIRMED' && can('checkin')) {
      return { label: 'Check In', run: () => openCheckIn(row) };
    }
    if (row.bookingStatusCode === 'CHECKED_IN' && section === 'dueout' && can('checkout')) {
      return { label: 'Check Out', run: () => openCheckOut(row) };
    }
    if (can('view')) return { label: 'View Details', run: () => openView(row) };
    return null;
  }, [can, openCheckIn, openCheckOut, openView]);

  // --- Row actions (status- and RBAC-gated, mirrors BookingListScreen) ---
  const getRowActionsFor = useCallback((section: SectionKey) => (row: QueueRow): ActionMenuItem[] => {
    const items: ActionMenuItem[] = [];
    if (can('view')) {
      items.push({
        id: 'view', label: 'View Details', icon: <IconSystem.eye size={16} />,
        onClick: () => openView(row),
      });
    }
    if (can('checkin') && row.bookingStatusCode === 'CONFIRMED') {
      items.push({
        id: 'checkin', label: 'Check In', icon: <IconSystem.checkCircle size={16} />,
        onClick: () => openCheckIn(row),
      });
    }
    if (can('mark-no-show') && row.bookingStatusCode === 'CONFIRMED') {
      items.push({
        id: 'no-show', label: 'Mark as No-Show', icon: <IconSystem.alert size={16} />, variant: 'danger',
        onClick: () => withBooking(row.bookingId, setNoShowBooking),
      });
    }
    // Post-check-in amendment — same team, same permission as check-in itself.
    // GRC/guests/occupancy corrections until the last room departs.
    if (can('checkin') && row.bookingStatusCode === 'CHECKED_IN') {
      items.push({
        id: 'edit-checkin', label: 'Edit Check-In', icon: <IconSystem.edit size={16} />,
        onClick: () => openAmend(row),
      });
    }
    // Room-level no-show is decided AT CHECK-IN (BR-CHKIN-002 revised):
    // unassigned rooms are confirmed no-show there — an in-house booking can
    // never carry unaccounted rooms, so no separate action exists here.
    // Extra rooms for an in-house party = a NEW walk-in at today's rate,
    // linked to the parent booking (related_booking_id).
    if (can('add') && row.bookingStatusCode === 'CHECKED_IN') {
      items.push({
        id: 'add-rooms-walkin', label: 'Add Rooms (Walk-In)', icon: <IconSystem.add size={16} />,
        onClick: () => {
          setWalkInLink({ id: row.bookingId, number: row.bookingNumber });
          setQuickWalkInOpen(true);
        },
      });
    }
    // The registration card (GRC-2): reprints carry the same number; each print is audited.
    if (can('view') && (row.bookingStatusCode === 'CHECKED_IN' || row.bookingStatusCode === 'CHECKED_OUT')) {
      items.push({
        id: 'print-grc', label: 'Print GRC', icon: <IconSystem.print size={16} />,
        onClick: () => { void openPdf(() => outputProfileService.grcPdf(row.bookingId), 'The registration card did not print.'); },
      });
    }
    // Room charging off: outlets collect directly — group rooms shared by several guests.
    if (row.bookingStatusCode === 'CHECKED_IN' && row.folioId && canControlRoomCharging()) {
      const folioId = row.folioId;
      items.push({
        id: 'room-charging', label: row.folioNoPost ? 'Room Charging (off)…' : 'Room Charging…',
        icon: <IconSystem.creditCard size={16} />,
        onClick: () => { void openRoomCharging(folioId); },
      });
    }
    if (can('checkout') && row.bookingStatusCode === 'CHECKED_IN') {
      items.push({
        id: 'checkout', label: 'Check Out', icon: <IconSystem.checkCircle size={16} />,
        onClick: () => openCheckOut(row),
      });
    }
    // The rooms have left; the account is settled and invoiced (2026-10-16).
    if (row.bookingStatusCode === 'CHECKED_OUT' && invoiceActions().includes('view')) {
      items.push({
        id: 'settle-invoice', label: 'Settle / Invoice', icon: <IconSystem.file size={16} />,
        onClick: () => setInvoiceFor(String(row.bookingId)),
      });
    }
    if (can('extend-stay') && row.bookingStatusCode === 'CHECKED_IN' && section !== 'arrivals') {
      items.push({
        id: 'extend', label: 'Extend Stay', icon: <IconSystem.calendar size={16} />,
        onClick: () => withBooking(row.bookingId, setExtendBooking),
      });
    }
    return items;
  }, [can, withBooking, openView, openCheckIn, openCheckOut, openAmend, openRoomCharging]);

  // --- Cell renderers ---
  const statusCell = useCallback((row: FrontOfficeQueueItem) => {
    const color = STATUS_PILL_COLORS[row.bookingStatusColor || 'secondary'] || STATUS_PILL_COLORS.secondary;
    return (
      <span style={{
        display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '999px',
        fontSize: '0.72rem', fontWeight: 600, color, background: `${color}1f`,
      }}>
        {row.bookingStatus || row.bookingStatusCode}
      </span>
    );
  }, []);

  const guestCell = useCallback((row: FrontOfficeQueueItem) => (
    <span>
      {row.guestFullName || '—'}
      {row.isVip && (
        <span style={{ marginLeft: '0.35rem', fontSize: '0.68rem', fontWeight: 700, color: '#7c3aed' }}>★ VIP</span>
      )}
      {row.partyName && (
        <span style={{ display: 'block', fontSize: '0.68rem', color: 'var(--text-muted)' }}>{row.partyName}</span>
      )}
      {/* Registration card completed after check-in (GRC-3): what is missing, and a foreign guest's Form C deadline. */}
      {row.grc?.pending && (() => {
        const formC = formCText(row.grc.formCDueAt);
        return (
          <span style={{ display: 'flex', flexWrap: 'wrap', gap: '0.25rem', marginTop: '0.15rem' }}>
            <span title={`Missing: ${row.grc.missing.join(', ')} — complete on Edit Check-In`} style={{
              padding: '0 0.4rem', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700,
              color: '#92400e', background: 'rgba(245,158,11,0.18)',
            }}>GRC pending</span>
            {formC && (
              <span style={{
                padding: '0 0.4rem', borderRadius: '999px', fontSize: '0.65rem', fontWeight: 700,
                color: formC.overdue ? '#b91c1c' : '#92400e',
                background: formC.overdue ? 'rgba(220,38,38,0.12)' : 'rgba(245,158,11,0.18)',
              }}>{formC.text}</span>
            )}
          </span>
        );
      })()}
    </span>
  ), []);

  /**
   * Folio state. A checked-in booking with NO folio is a defect — the account is
   * opened inside the check-in transaction, so its absence means something went
   * wrong. Showing that as a red pill across the whole in-house list is the point
   * of carrying the column: one systemic failure, one look, rather than opening a
   * folio screen per guest.
   */
  const folioCell = useCallback((row: FrontOfficeQueueItem) => {
    if (!row.folioNumber) {
      return row.bookingStatusCode === 'CHECKED_IN'
        ? (
          <span style={{
            display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '999px',
            fontSize: '0.7rem', fontWeight: 700, color: '#dc2626', background: 'rgba(220,38,38,0.10)',
          }}>no folio</span>
        )
        : <span style={{ color: 'var(--text-muted)' }}>—</span>;
    }
    const bal = row.folioBalance ?? 0;
    return (
      <span style={{ fontVariantNumeric: 'tabular-nums' }}>
        {row.folioNumber}
        <span style={{ display: 'block', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
          {bal < 0 ? `Cr ₹${Math.abs(bal).toLocaleString('en-IN')}`
            : bal > 0 ? `Due ₹${bal.toLocaleString('en-IN')}`
            : 'Nil'}
        </span>
        {row.folioNoPost && (
          <span title="Room charging off — the restaurant and other outlets collect directly" style={{
            display: 'inline-block', marginTop: '0.1rem', padding: '0 0.4rem', borderRadius: '999px',
            fontSize: '0.65rem', fontWeight: 700, color: '#92400e', background: 'rgba(245,158,11,0.18)',
          }}>No room charge</span>
        )}
      </span>
    );
  }, []);

  /** Rooms departed vs held — the visible consequence of per-room departure. */
  const departureCell = useCallback((row: FrontOfficeQueueItem) => {
    const total = row.roomsDeparted + row.roomsOccupied;
    if (total === 0) return <span style={{ color: 'var(--text-muted)' }}>—</span>;
    const done = row.roomsOccupied === 0;
    const started = row.roomsDeparted > 0;
    const tone = done ? '#16a34a' : '#d97706';
    return (
      <span style={{
        display: 'inline-block', padding: '0.1rem 0.5rem', borderRadius: '999px',
        fontSize: '0.72rem', fontWeight: 600,
        color: started ? tone : 'var(--text-secondary)',
        background: started ? `${tone}1f` : 'transparent',
        border: started ? 'none' : '1px solid var(--border-color)',
      }}>
        {row.roomsDeparted} / {total}{started ? ' departed' : ''}
      </span>
    );
  }, []);

  const clickableCell = useCallback((row: QueueRow, section: SectionKey, content: React.ReactNode, asLink = false) => {
    const primary = primaryActionFor(row, section);
    if (!primary) return <>{content}</>;
    return (
      <span
        role="button"
        tabIndex={0}
        onClick={primary.run}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); primary.run(); } }}
        title={`Click to ${primary.label}`}
        style={{ cursor: 'pointer', ...(asLink ? { color: '#2563eb', fontWeight: 600 } : {}) }}
      >
        {content}
      </span>
    );
  }, [primaryActionFor]);

  /*
   * Each section keeps its OWN columns. Arrivals need ETA and advance; in-house
   * needs room, plan and folio; due-out needs departure progress. One flat column
   * set serves none of them well — which is why the sheet is grouped rather than
   * a single table with a status column.
   *
   * The primary reference also changes at check-in: before arrival everyone
   * quotes the booking number, afterwards the GRC off the registration card. So
   * arrivals lead with the booking number and the other two with GRC.
   */
  const columnsFor = useCallback((section: SectionKey): SyutGridColumn<QueueRow>[] => {
    const guestCol: SyutGridColumn<QueueRow> = {
      id: 'guest', header: 'Guest / Account', accessorKey: 'guestFullName', enableSorting: false,
      cell: ({ row }) => guestCell(row.original),
    };
    const grcCol: SyutGridColumn<QueueRow> = {
      id: 'grc', header: 'GRC', size: 170, enableSorting: false,
      cell: ({ row }) => clickableCell(row.original as QueueRow, section, (
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.original.grcNumber || row.original.bookingNumber}
          <span style={{ display: 'block', fontSize: '0.68rem', color: 'var(--text-muted)' }}>
            {row.original.grcNumber ? row.original.bookingNumber : 'GRC pending'}
          </span>
        </span>
      ), true),
    };
    const roomsCol: SyutGridColumn<QueueRow> = {
      id: 'rooms', header: 'Rooms', accessorKey: 'roomNumbers', size: 120, enableSorting: false,
      cell: ({ row }) => <strong>{row.original.roomNumbers || '—'}</strong>,
    };
    const paxCol: SyutGridColumn<QueueRow> = {
      id: 'pax', header: 'Pax', size: 80, enableSorting: false,
      cell: ({ row }) => <span>{row.original.numAdults}A{row.original.numChildren ? ` ${row.original.numChildren}C` : ''}</span>,
    };
    const folioCol: SyutGridColumn<QueueRow> = {
      id: 'folio', header: 'Folio', size: 155, enableSorting: false,
      cell: ({ row }) => folioCell(row.original),
    };
    // Open change items (FO requests + unpriced arrival changes) — closes the
    // loop for the desk without opening anything.
    const requestsCol: SyutGridColumn<QueueRow> = {
      id: 'requests', header: 'Requests', size: 95, enableSorting: false,
      cell: ({ row }) => {
        const n = row.original.openChangeRequests ?? 0;
        return n > 0 ? (
          <span
            title={`${n} open change item(s) with booking staff — details in Edit Check-In`}
            style={{
              fontSize: '0.66rem', fontWeight: 700, padding: '0.1rem 0.5rem',
              borderRadius: '999px', background: 'rgba(245,158,11,.12)',
              color: '#b45309', border: '1px solid rgba(245,158,11,.4)',
              whiteSpace: 'nowrap',
            }}
          >
            {n} OPEN
          </span>
        ) : <span style={{ color: 'var(--text-muted)' }}>—</span>;
      },
    };

    if (section === 'arrivals') {
      return [
        {
          id: 'bookingNumber', header: 'Booking', accessorKey: 'bookingNumber', size: 145, enableSorting: false,
          cell: ({ row }) => clickableCell(row.original as QueueRow, section, row.original.bookingNumber, true),
        },
        guestCol,
        { id: 'category', header: 'Rooms booked', accessorKey: 'categorySummary', enableSorting: false },
        paxCol,
        {
          id: 'eta', header: 'ETA', size: 80, enableSorting: false,
          cell: ({ row }) => <span>{fmtTime(row.original.expectedArrivalTime)}</span>,
        },
        {
          id: 'advance', header: 'Advance', size: 125, enableSorting: false,
          cell: ({ row }) => {
            const adv = row.original.advanceReceived ?? 0;
            return adv > 0
              ? <span style={{ color: '#16a34a', fontWeight: 600 }}>₹ {adv.toLocaleString('en-IN')}</span>
              : <span style={{ color: 'var(--text-muted)' }}>—</span>;
          },
        },
        {
          id: 'status', header: 'Status', size: 125, enableSorting: false,
          cell: ({ row }) => clickableCell(row.original as QueueRow, section, statusCell(row.original)),
        },
      ];
    }

    if (section === 'dueout') {
      return [
        grcCol, guestCol, roomsCol, paxCol,
        {
          id: 'outby', header: 'Out by', size: 110, enableSorting: false,
          cell: ({ row }) => <span>{fmtDate(row.original.checkOutDate)}</span>,
        },
        {
          id: 'departed', header: 'Rooms departed', size: 155, enableSorting: false,
          cell: ({ row }) => departureCell(row.original),
        },
        folioCol,
      ];
    }

    // In-house
    return [
      grcCol, guestCol, roomsCol, paxCol, requestsCol,
      { id: 'plan', header: 'Plan', size: 90, accessorKey: 'mealPlanSummary', enableSorting: false },
      {
        id: 'departs', header: 'Departs', size: 120, enableSorting: false,
        cell: ({ row }) => {
          const isToday = fmtDate(row.original.checkOutDate) === toDateInput(new Date());
          return (
            <span style={isToday ? { color: '#d97706', fontWeight: 650 } : undefined}>
              {fmtDate(row.original.checkOutDate)}{isToday ? ' · today' : ''}
            </span>
          );
        },
      },
      folioCol,
    ];
  }, [guestCell, statusCell, clickableCell, folioCell, departureCell]);

  // --- Toolbar ---
  const actionButtons: ActionButton[] = useMemo(() => {
    const buttons: ActionButton[] = [];
    if (can('add')) {
      buttons.push({
        id: 'walk-in', icon: <IconSystem.add size={16} />, label: 'Walk-In',
        onClick: () => {
          if (!propertyId) {
            notificationService.toast.warning('Select a property first.');
            return;
          }
          setWalkInLink(null);
          setQuickWalkInOpen(true);
        },
        variant: 'primary',
      });
    }
    buttons.push({
      id: 'refresh', icon: <IconSystem.refresh size={16} />, label: 'Refresh',
      onClick: refreshAll, variant: 'icon',
    });
    return buttons;
  }, [can, refreshAll, propertyId]);

  // --- Room board grouping ---
  const boardByCategory = useMemo(() => {
    const groups = new Map<string, RoomBoardRoom[]>();
    boardRooms.forEach((r) => {
      const key = r.categoryName || r.categoryCode || 'Uncategorised';
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(r);
    });
    return Array.from(groups.entries());
  }, [boardRooms]);

  const handleBoardCellClick = useCallback((room: RoomBoardRoom) => {
    if (room.isOoo) return; // OOO managed via the OOO register, not the board
    if (room.isOccupied) {
      if (can('room-move')) setMoveRoom(room);
      else if (can('view') && room.occupantBookingId) withBooking(room.occupantBookingId, setViewBooking);
    } else if (can('room-status')) {
      setStatusRoom(room);
    }
  }, [can, withBooking]);

  // --- Tabs strip ---
  const tabs: { id: FoTab; label: string; count?: number }[] = useMemo(() => [
    { id: 'dashboard', label: 'Dashboard' },
    {
      id: 'daysheet',
      label: 'Day Sheet',
      count: summary?.totalBookings,
    },
    { id: 'roomboard', label: 'Room Board' },
  ], [summary]);

  const switchTab = useCallback((tab: FoTab) => {
    setActiveTab(tab);
    setSearchTerm('');
  }, []);

  const toggleSection = useCallback((key: SectionKey) => {
    setSections((prev) => ({ ...prev, [key]: { ...prev[key], collapsed: !prev[key].collapsed } }));
  }, []);

  /*
   * Rooms + pax beside the booking count. Server-aggregated over the whole
   * filtered set, so it does not shift as the operator pages.
   *
   * Due Out says "rooms left" and counts only rooms NOT yet departed: departure
   * is per room, so on a sheet where everyone has already gone the honest number
   * is zero, and that is exactly the number housekeeping acts on.
   */
  const sectionTotals = useCallback((key: SectionKey): string | null => {
    const s = key === 'arrivals' ? summary?.arrivals
            : key === 'inhouse' ? summary?.inHouse
            : summary?.dueOut;
    if (!s) return null;
    const roomLabel = key === 'dueout'
      ? `${s.rooms} room${s.rooms === 1 ? '' : 's'} left`
      : `${s.rooms} room${s.rooms === 1 ? '' : 's'}`;
    return `· ${roomLabel} · ${s.pax} pax`;
  }, [summary]);

  const renderSection = useCallback((key: SectionKey) => {
    const meta = SECTION_META[key];
    const data = sections[key];
    return (
      <div key={key} style={{ marginBottom: '0.9rem' }}>
        <div
          role="button"
          tabIndex={0}
          onClick={() => toggleSection(key)}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSection(key); } }}
          style={{
            display: 'flex', alignItems: 'baseline', gap: '0.6rem', cursor: 'pointer', userSelect: 'none',
            padding: '0.45rem 0.7rem', background: 'var(--bg-tertiary)',
            border: '1px solid var(--border-color)', borderRadius: data.collapsed ? '6px' : '6px 6px 0 0',
          }}
        >
          <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>{data.collapsed ? '▶' : '▼'}</span>
          <span style={{
            fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em',
            textTransform: 'uppercase', color: 'var(--text-primary)',
          }}>{meta.title}</span>
          <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{data.total}</span>
          {sectionTotals(key) && (
            <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
              {sectionTotals(key)}
            </span>
          )}
          <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--text-muted)' }}>{meta.hint}</span>
        </div>
        {!data.collapsed && (
          <SyutGrid
            data={data.items}
            columns={columnsFor(key)}
            isLoading={data.loading}
            enablePagination
            manualPagination
            totalCount={data.total}
            currentPage={data.page - 1}
            pageSize={pageSize}
            onPageChange={(idx: number) => loadSection(key, idx + 1, pageSize, queueDate)}
            onPageSizeChange={(size: number) => { setPageSize(size); loadSection(key, 1, size, queueDate); }}
            rowActions={getRowActionsFor(key)}
            frozenRightColumns={['actions']}
            emptyMessage={meta.empty}
          />
        )}
      </div>
    );
  }, [sections, toggleSection, columnsFor, pageSize, queueDate, loadSection, getRowActionsFor, sectionTotals]);

  // --- Dashboard tile ---
  const tile = (label: string, value: React.ReactNode, sub: string, onClick?: () => void, accent?: string) => (
    <div
      key={label}
      onClick={onClick}
      role={onClick ? 'button' : undefined}
      tabIndex={onClick ? 0 : undefined}
      onKeyDown={onClick ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
      style={{
        background: 'var(--bg-primary)', border: '1px solid var(--border-color)',
        borderRadius: '10px', padding: '0.9rem 1rem', cursor: onClick ? 'pointer' : 'default',
        userSelect: 'none', borderLeft: accent ? `3px solid ${accent}` : undefined,
      }}
    >
      <div style={{ fontSize: '0.7rem', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--text-muted)' }}>{label}</div>
      <div style={{ fontSize: '1.6rem', fontWeight: 750, color: 'var(--text-primary)', margin: '0.15rem 0' }}>{value}</div>
      <div style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>{sub}</div>
    </div>
  );

  return (
    <div className="module-master-container">
      <ModuleHeader
        title="Front Office"
        description="Check-ins, check-outs, walk-ins, room allocation and room status."
        icon={<IconSystem.home size={18} />}
        leadingToolbarItems={tabId ? <MinimizedModalStrip tabId={tabId} /> : undefined}
        actions={actionButtons}
      />

      <div className="module-content" style={{ marginTop: '6px' }}>
        {/* minHeight: 0 is load-bearing on every flex level here — without it the
            grid/board content forces the column past the viewport, hiding the
            pagination footer and the app footer with no scrollbar. */}
        <div style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden', minHeight: 0 }}>
          {/* Context bar: property + tabs + date + search */}
          <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center', flexWrap: 'wrap', padding: '0 0 0.5rem 0' }}>
            <div style={{ minWidth: '240px' }}>
              <SyutSelect
                options={propertyOptions}
                value={propertyId}
                onChange={(val) => { setPropertyId((val as string) || ''); }}
                placeholder="Select property…"
                size="sm"
              />
            </div>
            <div style={{ display: 'flex', gap: '0.25rem', background: 'var(--bg-secondary)', borderRadius: '8px', padding: '0.2rem' }}>
              {tabs.map((t) => (
                <div
                  key={t.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => switchTab(t.id)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); switchTab(t.id); } }}
                  style={{
                    padding: '0.35rem 0.85rem', borderRadius: '6px', fontSize: '0.8rem',
                    fontWeight: activeTab === t.id ? 700 : 500, cursor: 'pointer', userSelect: 'none',
                    background: activeTab === t.id ? 'var(--bg-primary)' : 'transparent',
                    color: activeTab === t.id ? 'var(--text-primary)' : 'var(--text-secondary)',
                    boxShadow: activeTab === t.id ? '0 1px 2px rgba(0,0,0,0.12)' : 'none',
                  }}
                >
                  {t.label}{typeof t.count === 'number' && t.count > 0 ? ` (${t.count})` : ''}
                </div>
              ))}
            </div>

            {/* The date applies to arrivals and due-out together now. It used to
                live on the Arrivals tab alone, which meant switching tabs
                silently changed which day you were looking at. */}
            {activeTab === 'daysheet' && (
              <div style={{ display: 'flex', gap: '0.25rem', background: 'var(--bg-secondary)', borderRadius: '8px', padding: '0.2rem' }}>
                {(['yesterday', 'today', 'tomorrow'] as QueueDate[]).map((d) => (
                  <div
                    key={d}
                    role="button"
                    tabIndex={0}
                    onClick={() => setQueueDate(d)}
                    onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setQueueDate(d); } }}
                    style={{
                      padding: '0.35rem 0.75rem', borderRadius: '6px', fontSize: '0.78rem',
                      fontWeight: queueDate === d ? 700 : 500, cursor: 'pointer', userSelect: 'none',
                      background: queueDate === d ? 'var(--bg-primary)' : 'transparent',
                      color: queueDate === d ? 'var(--text-primary)' : 'var(--text-secondary)',
                    }}
                  >
                    {QUEUE_DATE_LABELS[d]}
                  </div>
                ))}
              </div>
            )}

            {activeTab === 'daysheet' && (
              <div style={{ flex: '1 1 220px', minWidth: '200px' }}>
                <input
                  type="text"
                  className="form-input"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search guest, room, GRC or booking…"
                  style={{ fontSize: '0.8125rem', padding: '0.375rem 0.625rem' }}
                />
              </div>
            )}

            {activeTab === 'daysheet' && queueDate === 'yesterday' && !searchResults && (
              <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                Backdated view — for after-midnight arrivals and migration entry. Set the actual
                check-in date/time in the Check-In dialog.
              </span>
            )}
          </div>

          {/* ============ DASHBOARD ============ */}
          {activeTab === 'dashboard' && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', paddingBottom: '1rem' }}>
              {dashboard ? (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.75rem' }}>
                    {tile('Rooms Occupied', `${dashboard.roomsOccupied} / ${dashboard.roomsTotal}`,
                      `${dashboard.roomsAvailable} available · ${dashboard.roomsOoo} OOO · ${dashboard.roomsDirty} dirty`,
                      () => switchTab('roomboard'), '#2563eb')}
                    {tile('Arrivals Today', dashboard.arrivalsExpected,
                      `${dashboard.arrivalsCompleted} arrived`, () => switchTab('daysheet'), '#16a34a')}
                    {tile('Departures Today', dashboard.departuresExpected,
                      `${dashboard.departuresCompleted} departed`, () => switchTab('daysheet'), '#d97706')}
                    {tile('In-House', dashboard.inHouseBookings,
                      `${dashboard.inHouseAdults} adults · ${dashboard.inHouseChildren} children`
                      + (dashboard.inHouseVip ? ` · ${dashboard.inHouseVip} VIP` : ''),
                      () => switchTab('daysheet'), '#7c3aed')}
                  </div>
                  <div style={{ marginTop: '0.75rem', fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                    Counts are property-scoped for today. Tap a tile to open the Day Sheet.
                  </div>

                  {/* Week-ahead availability — the booking form's calendar panel
                      anchored on today (no stay window to highlight), with the
                      house-level Total free / Occupancy footer the desk plans
                      staffing from. Read-only by design: a reference grid that
                      doubles as a launcher invites mis-taps at the desk. */}
                  <div style={{ marginTop: '1rem' }}>
                    <AvailabilityCalendarPanel
                      propertyId={propertyId}
                      title="Availability — week ahead"
                      showTotals
                      refreshToken={availRefreshToken}
                    />
                  </div>
                </>
              ) : (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  {propertyId ? 'Loading dashboard…' : 'Select a property to begin.'}
                </div>
              )}
            </div>
          )}

          {/* ============ DAY SHEET ============ */}
          {activeTab === 'daysheet' && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', paddingBottom: '1rem' }}>
              {!propertyId && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  Select a property to begin.
                </div>
              )}

              {/* Search replaces the groups rather than filtering inside them:
                  the operator searching has stopped working a queue and is
                  serving whoever is in front of them. */}
              {propertyId && searchResults && (
                <>
                  <div style={{
                    display: 'flex', alignItems: 'baseline', gap: '0.6rem',
                    padding: '0.45rem 0.7rem', background: 'var(--bg-tertiary)',
                    border: '1px solid var(--border-color)', borderRadius: '6px 6px 0 0',
                  }}>
                    <span style={{
                      fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em',
                      textTransform: 'uppercase', color: 'var(--text-primary)',
                    }}>Search results</span>
                    <span style={{ fontSize: '0.78rem', color: 'var(--text-secondary)' }}>{searchResults.length}</span>
                    <span style={{ marginLeft: 'auto', fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                      All states, any date
                    </span>
                  </div>
                  <SyutGrid
                    data={searchResults}
                    columns={columnsFor('inhouse')}
                    isLoading={searching}
                    rowActions={getRowActionsFor('inhouse')}
                    frozenRightColumns={['actions']}
                    emptyMessage="Nothing matched. Try a room number, surname or booking number."
                  />
                </>
              )}

              {propertyId && !searchResults
                && (['arrivals', 'inhouse', 'dueout'] as SectionKey[]).map(renderSection)}
            </div>
          )}

          {/* ============ ROOM BOARD ============ */}
          {activeTab === 'roomboard' && (
            <div style={{ flex: 1, minHeight: 0, overflow: 'auto', paddingBottom: '1rem' }}>
              <div style={{ display: 'flex', gap: '0.9rem', flexWrap: 'wrap', fontSize: '0.72rem', color: 'var(--text-secondary)', padding: '0.1rem 0 0.6rem' }}>
                {Object.entries(BOARD_STATE_COLORS).map(([state, c]) => (
                  <span key={state} style={{ display: 'inline-flex', alignItems: 'center', gap: '0.3rem' }}>
                    <span style={{ width: 10, height: 10, borderRadius: 3, background: c.bg, border: `1.5px solid ${c.border}`, display: 'inline-block' }} />
                    {c.label}
                  </span>
                ))}
                <span style={{ color: 'var(--text-muted)' }}>
                  Click a free room to set housekeeping status · click an occupied room to move the guest.
                </span>
              </div>
              {boardByCategory.map(([category, rooms]) => (
                <div key={category} style={{ marginBottom: '1rem' }}>
                  <div style={{ fontSize: '0.78rem', fontWeight: 700, color: 'var(--text-secondary)', margin: '0 0 0.4rem 0.1rem' }}>
                    {category} <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>({rooms.length} rooms)</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(120px, 1fr))', gap: '0.5rem' }}>
                    {rooms.map((room) => {
                      const c = BOARD_STATE_COLORS[room.derivedState] || BOARD_STATE_COLORS.AVAILABLE;
                      return (
                        <div
                          key={room.roomId}
                          role="button"
                          tabIndex={0}
                          title={room.isOoo ? `OOO — ${room.oooReason || 'out of order'}`
                            : room.isOccupied ? `${room.occupantGuestName || ''} · out ${fmtDate(room.occupantCheckOutDate)}`
                            : c.label}
                          onClick={() => handleBoardCellClick(room)}
                          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleBoardCellClick(room); } }}
                          style={{
                            border: `1.5px solid ${c.border}`, background: c.bg, borderRadius: '8px',
                            padding: '0.5rem 0.6rem', cursor: room.isOoo ? 'not-allowed' : 'pointer', userSelect: 'none',
                            minHeight: '58px',
                          }}
                        >
                          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                            <span style={{ fontWeight: 750, fontSize: '0.9rem', color: 'var(--text-primary)' }}>{room.roomNumber}</span>
                            <span style={{ fontSize: '0.62rem', fontWeight: 700, color: c.border }}>{c.label.toUpperCase()}</span>
                          </div>
                          <div style={{ fontSize: '0.68rem', color: 'var(--text-secondary)', marginTop: '0.15rem', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                            {room.isOccupied
                              ? `${room.occupantGuestName || room.occupantBookingNumber} · out ${fmtDate(room.occupantCheckOutDate)}`
                              : room.isOoo ? (room.oooReason || 'Out of order')
                              : (room.floorNumber ? `Floor ${room.floorNumber}` : ' ')}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              {boardRooms.length === 0 && !loading && (
                <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--text-muted)' }}>
                  {propertyId ? 'No active rooms for this property.' : 'Select a property to load the room board.'}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* ============ MODALS ============ */}
      <QuickCheckInModal
        isOpen={!!checkInBooking}
        booking={checkInBooking}
        onClose={() => setCheckInBooking(null)}
        onCheckedIn={handleLifecycleDone}
        canChangeCategory={can('room-category-change')}
        canChangeArrival={can('arrival-changes')}
        tabId={tabId}
        moduleName="Front Office"
        onRestoreCheckIn={(b) => setCheckInBooking(b)}
      />
      {/* Post-check-in amend — same modal, amend mode, own session state so a
          parked check-in and a parked amendment never collide. */}
      <QuickCheckInModal
        isOpen={!!amendBooking}
        booking={amendBooking}
        mode="amend"
        onClose={() => setAmendBooking(null)}
        onCheckedIn={handleLifecycleDone}
        canChangeCategory={false}
        canChangeArrival={can('arrival-changes')}
        tabId={tabId}
        moduleName="Front Office"
        onRestoreCheckIn={(b) => setAmendBooking(b)}
      />
      <IssueInvoicesModal isOpen={!!invoiceFor} bookingId={invoiceFor}
        onClose={() => setInvoiceFor(null)} onIssued={handleLifecycleDone} />
      <RoomChargingModal
        isOpen={!!chargingFolio}
        label={chargingFolio ? `${chargingFolio.folioNumber} · ${chargingFolio.bookingNumber}` : ''}
        folioId={chargingFolio?.folioId}
        noPost={!!chargingFolio?.noPost}
        creditLimit={chargingFolio?.creditLimit}
        onClose={() => setChargingFolio(null)}
        onSaved={() => { setChargingFolio(null); refreshAll(); }}
      />
      <CheckOutRoomPickerModal
        isOpen={!!checkOutBooking}
        booking={checkOutBooking}
        onClose={() => setCheckOutBooking(null)}
        onCheckedOut={handleLifecycleDone}
      />
      <MarkNoShowConfirmDialog
        isOpen={!!noShowBooking}
        booking={noShowBooking}
        onClose={() => setNoShowBooking(null)}
        onMarked={handleLifecycleDone}
      />
      <ExtendStayModal
        isOpen={!!extendBooking}
        booking={extendBooking}
        onClose={() => setExtendBooking(null)}
        onExtended={handleLifecycleDone}
      />
      <BookingViewModal
        collectionPoint="FRONT_OFFICE"
        isOpen={!!viewBooking}
        booking={viewBooking}
        onClose={() => setViewBooking(null)}
      />
      <RoomStatusModal
        isOpen={!!statusRoom}
        room={statusRoom}
        onClose={() => setStatusRoom(null)}
        onUpdated={() => { loadBoard(); loadDashboard(); }}
      />
      <RoomMoveModal
        isOpen={!!moveRoom}
        fromRoom={moveRoom}
        boardRooms={boardRooms}
        onClose={() => setMoveRoom(null)}
        onMoved={() => { loadBoard(); loadDashboard(); }}
      />
      {/* Walk-in: single-screen desk sheet — booking + advance + check-in in one action */}
      <WalkInModal
        isOpen={quickWalkInOpen}
        propertyId={propertyId}
        onClose={() => { setQuickWalkInOpen(false); setWalkInLink(null); }}
        onCompleted={refreshAll}
        relatedBookingId={walkInLink?.id}
        relatedBookingNumber={walkInLink?.number}
        // Same gate as check-in's category change — one permission for "may
        // alter what was quoted". The full-booking-form escape hatch is gone:
        // Walk-In now carries package selection and allocation itself, and two
        // entry points left the operator unsure which was authoritative.
        canEditRate={can('room-category-change')}
        tabId={tabId}
        moduleName="Front Office"
        onRestoreWalkIn={() => setQuickWalkInOpen(true)}
      />
    </div>
  );
};

export default FrontOfficeWorkspace;
